import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { nanoid } from 'nanoid';
import styles from '../../styles/query/AddFilterMenu.module.scss';

/**
 * Add Filter Menu Component
 * 
 * Shows a menu/dialog to select a field to add as a filter
 * 
 * @param {Object} props
 * @param {Object} props.schema - Query schema with available fields
 * @param {Array} props.existingFilters - Array of existing filter field names
 * @param {function} props.onSelect - Handler when a field is selected (receives field object)
 * @param {function} props.onClose - Handler to close the menu
 * @param {Object} props.anchorEl - Element to anchor menu to
 */
export function AddFilterMenu({
  schema,
  existingFilters = [],
  onSelect,
  onClose,
  anchorEl
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const menuRef = useRef(null);

  const availableFields = React.useMemo(() => {
    if (!schema?.fields) return [];
    
    const existingFieldNames = existingFilters.map(f => {
      if (typeof f === 'object' && f !== null && 'field' in f) {
        return f.field ?? null;
      }
      return typeof f === 'string' ? f : null;
    }).filter(name => name !== null);
    
    return schema.fields
      .filter(field => {
        if (existingFieldNames.includes(field.name)) return false;
        
        if (searchTerm.trim()) {
          const term = searchTerm.toLowerCase();
          const label = (field.label || field.name || '').toLowerCase();
          const description = (field.description || '').toLowerCase();
          return label.includes(term) || description.includes(term);
        }
        
        return true;
      })
      .sort((a, b) => {
        const labelA = (a.label || a.name || '').toLowerCase();
        const labelB = (b.label || b.name || '').toLowerCase();
        return labelA.localeCompare(labelB);
      });
  }, [schema, existingFilters, searchTerm]);

  const calculatePosition = React.useCallback(() => {
    if (!anchorEl) return;
    
    const rect = anchorEl.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, 200);
    const menuMaxWidth = 400;
    const actualMenuWidth = Math.min(menuWidth, menuMaxWidth);
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const actualMenuHeight = menuRef.current?.offsetHeight || 400;
    
    let left = rect.left;
    if (left + actualMenuWidth > viewportWidth) {
      left = rect.right - actualMenuWidth;
    }
    if (left < 0) {
      left = 8;
    }
    
    let top = rect.bottom + 4;
    if (top + actualMenuHeight > viewportHeight) {
      top = rect.top - actualMenuHeight - 4;
      if (top < 0) {
        top = 8;
      }
    }
    
    setMenuPosition({
      top,
      left,
      width: actualMenuWidth
    });
  }, [anchorEl]);

  useEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  useEffect(() => {
    if (!anchorEl) return;

    const updatePosition = () => {
      requestAnimationFrame(() => {
        calculatePosition();
      });
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
    document.body.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
      document.body.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorEl, calculatePosition, availableFields.length, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        anchorEl &&
        !anchorEl.contains(event.target)
      ) {
        onClose();
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, anchorEl]);

  const handleFieldSelect = (field) => {
    const defaultOperator = (field.operators && Array.isArray(field.operators) && field.operators.length > 0)
      ? field.operators[0]
      : '=';
    
    const newFilter = {
      id: nanoid(),
      field: field.name,
      operator: defaultOperator,
      values: []
    };
    
    onSelect(newFilter);
    onClose();
  };

  if (!anchorEl || !schema) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className={styles.addFilterMenu}
      style={{
        position: 'fixed',
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
        width: `${menuPosition.width}px`,
        zIndex: 40
      }}
    >
      {/* Search Input */}
      <div className={styles.searchSection}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
      </div>

      {/* Field List */}
      <div className={styles.fieldList}>
        {availableFields.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyText}>
              {searchTerm ? 'No fields found' : 'All fields added'}
            </p>
          </div>
        ) : (
          availableFields.map((field) => (
            <button
              key={field.name}
              type="button"
              className={styles.fieldItem}
              onClick={() => handleFieldSelect(field)}
            >
              <span className={styles.fieldLabel}>{field.label || field.name}</span>
              {field.description && (
                <span className={styles.fieldDescription}>{field.description}</span>
              )}
            </button>
          ))
        )}
      </div>
    </div>,
    document.body
  );
}

export default AddFilterMenu;

